import { normalizeDirectoryTitle } from '../config/directoryTitle';
import type { DirectoryNodeKind, DirectoryTreeNode } from './types';

export function findNodeById(nodes: DirectoryTreeNode[], nodeId: string): DirectoryTreeNode | null {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children?.length) {
      const found = findNodeById(node.children, nodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export function updateNodeById(
  nodes: DirectoryTreeNode[],
  nodeId: string,
  updater: (node: DirectoryTreeNode) => DirectoryTreeNode
): DirectoryTreeNode[] {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      return updater(node);
    }
    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeById(node.children, nodeId, updater),
      };
    }
    return node;
  });
}

export function appendChildToTree(
  nodes: DirectoryTreeNode[],
  parentId: string,
  child: DirectoryTreeNode
): DirectoryTreeNode[] {
  return updateNodeById(nodes, parentId, (node) => ({
    ...node,
    children: [...(node.children ?? []), child],
  }));
}

export function extractNumericId(nodeId: string, prefix: string): number | undefined {
  if (!nodeId.startsWith(prefix)) {
    return undefined;
  }
  const parsed = Number(nodeId.slice(prefix.length));
  return Number.isInteger(parsed) ? parsed : undefined;
}

export function isFolderLikeKind(kind: DirectoryNodeKind): boolean {
  return kind === 'module' || kind === 'folder';
}

// No 'matrix': the server refuses moving a matrix link, which stays where its placement put it.
const MOVABLE_KINDS = new Set<DirectoryNodeKind>(['folder', 'document', 'record']);

export function isMovableKind(kind: DirectoryNodeKind): boolean {
  return MOVABLE_KINDS.has(kind);
}

export function isMovableNode(node: DirectoryTreeNode): boolean {
  return isMovableKind(node.kind) && node.matrixPlacementMoveLocked !== true;
}

export function getParentId(nodes: DirectoryTreeNode[], nodeId: string): string | null | undefined {
  const walk = (items: DirectoryTreeNode[], parent: string | null): string | null | undefined => {
    for (const node of items) {
      if (node.id === nodeId) {
        return parent;
      }
      if (node.children?.length) {
        const result = walk(node.children, node.id);
        if (result !== undefined) {
          return result;
        }
      }
    }
    return undefined;
  };

  return walk(nodes, null);
}

export function getSiblingNodes(nodes: DirectoryTreeNode[], nodeId: string): DirectoryTreeNode[] {
  const parentId = getParentId(nodes, nodeId);
  if (parentId === undefined) {
    return [];
  }
  if (parentId === null) {
    return nodes;
  }
  return findNodeById(nodes, parentId)?.children ?? [];
}

export function hasDuplicateSiblingTitle(
  nodes: DirectoryTreeNode[],
  nodeId: string,
  title: string,
  kind: DirectoryNodeKind
): boolean {
  const normalizedTitle = normalizeDirectoryTitle(title);
  return getSiblingNodes(nodes, nodeId).some(
    (sibling) =>
      sibling.id !== nodeId &&
      sibling.kind === kind &&
      normalizeDirectoryTitle(getNodeDisplayName(sibling)) === normalizedTitle
  );
}

export function isDescendant(
  nodes: DirectoryTreeNode[],
  ancestorId: string,
  maybeDescendantId: string
): boolean {
  const ancestor = findNodeById(nodes, ancestorId);
  if (!ancestor?.children?.length) {
    return false;
  }
  return findNodeById(ancestor.children, maybeDescendantId) !== null;
}

export function normalizeMoveNodeIds(nodes: DirectoryTreeNode[], nodeIds: string[]): string[] {
  const movableIds = nodeIds.filter((id) => {
    const node = findNodeById(nodes, id);
    return node ? isMovableNode(node) : false;
  });

  return movableIds.filter(
    (id) =>
      !movableIds.some((candidateId) => candidateId !== id && isDescendant(nodes, candidateId, id))
  );
}

function assignModuleId(node: DirectoryTreeNode, moduleId: number): DirectoryTreeNode {
  return {
    ...node,
    ...(node.kind !== 'module' ? { moduleId } : {}),
    ...(node.children?.length
      ? { children: node.children.map((child) => assignModuleId(child, moduleId)) }
      : {}),
  };
}

export function removeNodeById(
  nodes: DirectoryTreeNode[],
  nodeId: string
): { tree: DirectoryTreeNode[]; removed: DirectoryTreeNode | null } {
  let removed: DirectoryTreeNode | null = null;

  const filterAndRecurse = (items: DirectoryTreeNode[]): DirectoryTreeNode[] => {
    const result: DirectoryTreeNode[] = [];
    for (const node of items) {
      if (node.id === nodeId) {
        removed = node;
        continue;
      }
      if (node.children?.length) {
        result.push({ ...node, children: filterAndRecurse(node.children) });
      } else {
        result.push(node);
      }
    }
    return result;
  };

  const tree = filterAndRecurse(nodes);
  return { tree, removed };
}

export function moveNodesToParent(
  nodes: DirectoryTreeNode[],
  nodeIds: string[],
  targetParentId: string
): DirectoryTreeNode[] {
  const normalizedNodeIds = normalizeMoveNodeIds(nodes, nodeIds);
  let tree = nodes;
  const movedNodes: DirectoryTreeNode[] = [];

  for (const nodeId of normalizedNodeIds) {
    const { tree: nextTree, removed } = removeNodeById(tree, nodeId);
    if (removed) {
      tree = nextTree;
      movedNodes.push(removed);
    }
  }

  const targetNode = findNodeById(tree, targetParentId);
  const targetModuleId = targetNode?.moduleId;

  for (const node of movedNodes) {
    const nextNode =
      targetModuleId !== undefined && node.kind !== 'module'
        ? assignModuleId(node, targetModuleId)
        : node;
    tree = appendChildToTree(tree, targetParentId, nextNode);
  }

  return tree;
}

export function getNodeDisplayName(node: DirectoryTreeNode): string {
  return node.details?.title ?? node.name;
}
